import React, {useCallback, useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import SearchInput from "../../base/input/search_input";
import {AutoComplete} from "../../base/auto_complete/auto_complete";
import Chips, {ChipsIconPosition, ChipsTypes} from "../../base/chips/chips";
import {ReactComponent as Checkbox} from "../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as CheckboxActive} from "../../../../assets/images/check-box/checkbox-active.svg";

const ContentSearchSection = ({searchData, type}) => {
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const isMounted = useIsMounted();

    /**
     * Fetches the list of categories based on the given category type.
     * @type {function(*=): Promise<void>}
     */
    const getCategories = useCallback(async (type) => {
        setLoadingCategories(true);
        const response = await Api.getCmsCategoriesByType(type);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setCategories(response.data ?? []);
        }
        setLoadingCategories(false);
    }, [isMounted]);

    /**
     * Fetches the list of categories based on the given category type.
     */
    useEffect(() => {
        void getCategories(type);
    }, [getCategories, type]);

    /**
     * Changes the categories of the search filters
     * @type {function(*=, *): void}
     */
    const searchCategories = useCallback((e, _categories) => {
        searchData.setFilters(prevState => ({...prevState, categories: _categories}));
    }, [searchData])

    /**
     * Removes a given category from the list of selected categories
     * @type {function(*): void}
     */
    const removeCategory = useCallback((categoryId) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            categories: prevState.categories.filter(e => e.id !== categoryId),
        }));
    }, [searchData])

    /**
     * Renders the autocomplete chips for the selected categories
     * @type {function(*): *}
     */
    const renderChips = useCallback((val) => {
        return (
            <div className={'d-flex flex-wrap'}>
                {
                    val?.map(e => (
                        <div key={e.id} className={'mr-2 mt-2 content-search-chips'}>
                            <Chips
                                content={e.title}
                                type={ChipsTypes.remove}
                                onIconClick={() => removeCategory(e.id)}
                                iconPosition={ChipsIconPosition.end}
                            />
                        </div>

                    ))
                }
            </div>
        )
    }, [removeCategory])

    /**
     * Renders the options of the autocomplete component
     * @type {function(*, *, *): *}
     */
    const renderOption = useCallback((_, option, state) => {
        if (!option)
            return '';
        if (typeof option === 'string') {
            return (
                <p className='mx-2 p-2'>
                    {option}
                </p>
            );
        }
        return (
            <div className={'d-flex flex-row justify-content-between align-items-center w-100'}>
                <p className='mx-2 p-2'>{option.title ?? option.label}</p>
                {
                    state.selected
                        ? <CheckboxActive/>
                        : <Checkbox/>
                }
            </div>
        );
    }, [])

    /**
     * Changes the keywords, and finally sets the page to 1 so that the useEffect watcher of the paginationInfo
     * can call the api with the new data
     * @param keywords {string}
     */
    const searchByKeyword = useCallback((keywords) => {
        searchData.setFilters(prevState => ({...prevState, keywords: keywords?.trim()}));
    }, [searchData])

    return (
        <>
            <Row className='no-gutters align-items-center justify-content-center mt-3'>
                <Col xs={12} className={'px-2 mb-3'}>
                    <SearchInput
                        autoSearch
                        immediateSearch
                        loading={searchData.loading}
                        initialValue={searchData.filters?.keywords ?? ''}
                        search={searchByKeyword}
                        label={'Keyword'}
                    />
                </Col>
                <Col xs={12} className='px-2 mb-4'>
                    <AutoComplete
                        value={searchData.filters?.categories ?? []}
                        loading={loadingCategories}
                        onSelect={searchCategories}
                        label={'Categories'}
                        type={'text'}
                        options={categories ?? []}
                        optionSelected={(o, v) => o.title === v.title}
                        multiple={true}
                        renderOption={renderOption}
                        renderTags={renderChips}
                        disableCloseOnSelect={true}
                        classes={{option: 'content-search-autocomplete-option'}}
                        loadingText={
                            <div className='loading auto-complete'>
                                <div/>
                                <div/>
                                <div/>
                                <div/>
                            </div>
                        }
                    />
                </Col>
            </Row>
        </>
    );
}

export default ContentSearchSection;
